import { useState } from "react";
import { Layers, Coins, MapPin, Package, RotateCcw } from "lucide-react";

import { cn } from "@/lib/utils";
import { TdmKeywordPills } from "./TdmKeywordPills";
import { TdmKeywordsDialog } from "./TdmKeywordsDialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { FilterBar } from "@/components/filters/FilterBar";
import { TDM_CATEGORIES, TDM_SECTORS } from "@/lib/tdm-constants";
import type { TdmFilterState } from "@/hooks/useTdmFilters";
import type { TdmCategory, TdmSector } from "@/types/tdm";

function toggle<T>(arr: T[], v: T): T[] {
  return arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
}

function MultiPill<T extends string>({
  label,
  options,
  selected,
  onToggle,
  onClear,
}: {
  label: string;
  options: { key: T; label: string; color: string }[];
  selected: T[];
  onToggle: (v: T) => void;
  onClear: () => void;
}) {
  const active = selected.length > 0;
  const value =
    selected.length === 0
      ? "Todas"
      : selected.length === 1
        ? options.find((o) => o.key === selected[0])?.label ?? "1 sel."
        : `${selected.length} sel.`;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "flex items-center gap-2 rounded-sm border bg-card px-3 py-2 text-xs transition-colors cursor-pointer",
            active ? "border-primary/30 bg-primary/5 text-primary/80" : "hover:bg-accent/30",
          )}
        >
          <span className={cn("font-medium", active ? "text-primary/70" : "text-slate-400")}>
            {label}
          </span>
          <span className={cn("font-bold", active ? "text-primary" : "text-foreground")}>
            {value}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
            {label}
          </p>
          {active && (
            <button
              onClick={onClear}
              className="text-xs font-semibold text-primary hover:underline"
            >
              Limpiar
            </button>
          )}
        </div>
        <div className="flex flex-col gap-0.5">
          {options.map((o) => (
            <div
              key={o.key}
              role="button"
              tabIndex={0}
              onClick={() => onToggle(o.key)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onToggle(o.key);
                }
              }}
              className="flex cursor-pointer items-center gap-2.5 rounded-sm px-2 py-1.5 text-left hover:bg-accent"
            >
              <Checkbox checked={selected.includes(o.key)} className="pointer-events-none" />
              <span
                className="size-2.5 rounded-full"
                style={{ backgroundColor: o.color }}
              />
              <span className="text-sm">{o.label}</span>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function TdmFilterBar({
  filters,
  patch,
  onReset,
}: {
  filters: TdmFilterState;
  patch: (p: Partial<TdmFilterState>) => void;
  onReset: () => void;
}) {
  const [managing, setManaging] = useState(false);

  return (
    <div className="space-y-3 rounded-2xl bg-card p-4 shadow-sm">
      {/* Fila 1: recursos a buscar + métrica */}
      <div className="flex flex-wrap items-start gap-3">
        <TdmKeywordPills
          keywords={filters.keywords}
          onChange={(keywords) => patch({ keywords })}
          onManage={() => setManaging(true)}
        />

        {/* Switch de métrica Monto / Cantidad */}
        <div className="flex items-center rounded-full border bg-card p-0.5 text-xs font-semibold">
          <button
            onClick={() => patch({ metric: "amount" })}
            className={cn(
              "flex items-center gap-1.5 rounded-full px-3 py-1.5 transition-colors",
              filters.metric === "amount"
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Coins className="size-3.5" />
            Monto
          </button>
          <button
            onClick={() => patch({ metric: "quantity" })}
            className={cn(
              "flex items-center gap-1.5 rounded-full px-3 py-1.5 transition-colors",
              filters.metric === "quantity"
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Package className="size-3.5" />
            Cantidad
          </button>
        </div>

        {/* Categoría activa (solo en modo cantidad) */}
        {filters.metric === "quantity" && (
          <div className="flex items-center gap-1.5">
            <Layers className="size-3.5 text-muted-foreground" />
            <div className="flex items-center rounded-full border bg-card p-0.5 text-xs font-semibold">
              {TDM_CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  onClick={() => patch({ activeCategory: c.key })}
                  className={cn(
                    "rounded-full px-2.5 py-1.5 transition-colors",
                    filters.activeCategory === c.key
                      ? "text-white shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                  style={
                    filters.activeCategory === c.key
                      ? { backgroundColor: c.color }
                      : undefined
                  }
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* El departamento sale del domicilio de la entidad, no de dónde está
            la obra: esto reubica las del MTC y demás entidades nacionales. */}
        <button
          onClick={() => patch({ decentralizeLima: !filters.decentralizeLima })}
          title="El MTC, Provias y los ministerios tienen sede en Lima, así que todas sus obras figuran ahí. Esto las reubica al departamento que nombra la obra."
          className={cn(
            "flex items-center gap-1.5 rounded-sm border px-3 py-2 text-xs font-semibold transition-colors cursor-pointer",
            filters.decentralizeLima
              ? "border-primary/30 bg-primary/5 text-primary"
              : "text-muted-foreground hover:bg-accent",
          )}
        >
          <MapPin className="size-3.5" />
          Descentralizar Lima
        </button>

        <button
          onClick={onReset}
          className="ml-auto flex items-center gap-1.5 rounded-sm border px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent"
        >
          <RotateCcw className="size-3.5" />
          Reiniciar
        </button>
      </div>

      {/* Fila 2: filtros compartidos con Descubrimiento + categoría/sector */}
      <div className="flex flex-wrap items-center gap-2">
        <FilterBar
          value={filters}
          onChange={patch}
          showProvinceDistrict
          showEntity
          showStages
          showContratado
          showOnlyInterests={false}
        />
        <MultiPill<TdmCategory>
          label="Categoría"
          options={TDM_CATEGORIES}
          selected={filters.categories}
          onToggle={(v) => patch({ categories: toggle(filters.categories, v) })}
          onClear={() => patch({ categories: [] })}
        />
        <MultiPill<TdmSector>
          label="Sector"
          options={TDM_SECTORS}
          selected={filters.sectors}
          onToggle={(v) => patch({ sectors: toggle(filters.sectors, v) })}
          onClear={() => patch({ sectors: [] })}
        />
      </div>

      <TdmKeywordsDialog
        keywords={filters.keywords}
        open={managing}
        onOpenChange={setManaging}
        onSave={(keywords) => patch({ keywords })}
      />
    </div>
  );
}
